/**
 * Firebase initialization per landing-app.
 *
 * App isolata dal gestionale (`preventivatore-app/src/`) ma collegata allo
 * STESSO progetto Firebase (`preventivatoreweb`). Gli utenti creati qui
 * compaiono nella stessa Auth + Firestore del gestionale.
 *
 * Vedi: docs/superpowers/specs/2026-05-15-marketplace-fv-design.md sezione 2.1
 *
 * Due istanze Auth:
 *  - `auth` (default app): usata per email/password (signInWithEmailAndPassword
 *    post-publicSignup) e operazioni anonime (chiamate CF callable senza login).
 *  - `getMarketplaceAuth()` (app secondaria 'marketplace-auth'): usata per
 *    Google sign-in nel wizard. Replica il pattern di `welcome-auth` dell'app
 *    principale: isolare il sign-in OAuth da qualsiasi altro AuthState listener
 *    per evitare race condition e side-effect su altri flussi.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: 'AIzaSyDvkHoEe3kEb-3mhd8UYpnjsY5lfaTJduY',
  authDomain: 'preventivatoreweb.firebaseapp.com',
  projectId: 'preventivatoreweb',
  storageBucket: 'preventivatoreweb.firebasestorage.app',
  messagingSenderId: '864779882266',
  appId: '1:864779882266:web:3d2e56bd564da9ffd226a3',
  measurementId: 'G-JP5EE9BJEL',
};

// === Default app (email/password + CF callable anonime) ===
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Le CF marketplace sono in europe-west1 (vedi publicSignup.ts)
export const functions = getFunctions(app, 'europe-west1');

// === Marketplace-auth: istanza separata per il flusso signup pubblico (Google OAuth) ===
// Evita interferenze con eventuali altri AuthState listener della default app.
const MARKETPLACE_AUTH_APP_NAME = 'marketplace-auth';

function getMarketplaceAuthApp() {
  const existing = getApps().find((a) => a.name === MARKETPLACE_AUTH_APP_NAME);
  if (existing) return getApp(MARKETPLACE_AUTH_APP_NAME);
  return initializeApp(firebaseConfig, MARKETPLACE_AUTH_APP_NAME);
}

let marketplaceAuth: Auth | null = null;
export function getMarketplaceAuth(): Auth {
  if (!marketplaceAuth) {
    marketplaceAuth = getAuth(getMarketplaceAuthApp());
  }
  return marketplaceAuth;
}

// Functions istanza collegata a marketplace-auth: i callable che richiedono
// autenticazione (registerMarketplaceTenant) DEVONO usare questa, perche'
// httpsCallable allega l'idToken dell'utente loggato sull'app a cui Functions
// e' collegato. Pattern allineato a getWelcomeAuthFunctions del lead onboarding.
let marketplaceFunctions: Functions | null = null;
export function getMarketplaceFunctions(): Functions {
  if (!marketplaceFunctions) {
    marketplaceFunctions = getFunctions(getMarketplaceAuthApp(), 'europe-west1');
  }
  return marketplaceFunctions;
}

// Emulator hook (solo dev locale, opzionale)
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFunctionsEmulator(functions, 'localhost', 5001);
}
